import './LeftNav.css';

import { Activity, Collaborate, Dashboard, Events, Report, Settings, Tools } from '@carbon/icons-react';
import { Nav, NavbarBrand } from 'react-bootstrap';
import React, { FunctionComponent, useContext, useState } from 'react';
import {
    UserProfileContext,
    canAccessAdmin,
    canAuthorReports,
    canManageTeamMembers,
    canViewMonitoring,
    canViewUsageReport,
    isClientTrackCustomer,
    isGlobalAdmin,
    isHighestAdmin,
    isRoleValidForCustomerType,
    isSourceSystemAdminOrHigher,
} from '../../Shared/contexts/UserProfile';

import AppsButton from './AppsButton';
import LeftNavItem from './LeftNavItem';
import { LeftNavSubItem } from './NavigationModels';

const LeftNav: FunctionComponent = () => {
    const userProfile = useContext(UserProfileContext);

    const [reportsSubItems] = useState<LeftNavSubItem[]>([
        { label: 'Admin Reports', link: '/reports/globaladmin' },
        { label: 'Reports', link: '/reports' },
        { label: 'My Work', link: '/reports/mywork' },
        { label: 'Community Reports', link: '/reports/community' },
        { label: 'Public Reports', link: '/reports/public' },
    ]);

    const monitoringSubItems = [
        { label: 'Batch Runs', link: '/monitoring/batch-runs' },
        { label: 'Report Usage Metrics', link: '/monitoring/report-usage-metrics' },
        { label: 'Sync to ClientTrack', link: '/monitoring/canonicalid' },
    ];

    const teamMembersSubItems = [
        { label: 'Team Members', link: '/teammembers' },
        { label: 'Insights System Users', link: '/teammembers/insights-system-users' },
    ];

    return (
        <Nav className="navbar-nav sidebar">
            <NavbarBrand className="sidebar-brand sidebar-brand-dark-rounded logo-container">
                {isClientTrackCustomer(userProfile) && <AppsButton />}
                <div className="sidebar-brand-text logo-text">
                    <a href="/">
                        <img src="CW-Insights-Logo-White.svg" alt="ClientInsight" width="160" height="32"/>
                    </a>
                </div>
            </NavbarBrand>

            <hr className="my-2" />

            {!userProfile.environmentConfig.cwInsightsCustomer && (
                <LeftNavItem linkUrl={'/'}>
                    <Dashboard size={24} className="nav-item-contents" />
                    <div className="nav-item-contents">Dashboard</div>
                </LeftNavItem>
            )}

            {/* Reports - available to all roles */}
            <LeftNavItem
                linkUrl="/reports"
                expandable
                subItems={
                    canAuthorReports(userProfile)
                        ? reportsSubItems.filter((item) => {
                            // Hide Admin Reports and Public Reports for CW Insights customers
                            if (userProfile.environmentConfig.cwInsightsCustomer) {
                                if (item.label === 'Admin Reports' || item.label === 'Public Reports') {
                                    return false;
                                }
                            }
                            // Admin Reports only for Global Admin (standard customers)
                            if (item.label === 'Admin Reports' && !isGlobalAdmin(userProfile)) {
                                return false;
                            }
                            return true;
                        })
                        : reportsSubItems.filter((item) => {
                            // Hide Admin Reports and My Work for non-authors
                            if (item.label === 'Admin Reports' || item.label === 'My Work') {
                                return false;
                            }
                            // Hide Public Reports for CW Insights customers
                            if (userProfile.environmentConfig.cwInsightsCustomer && item.label === 'Public Reports') {
                                return false;
                            }
                            return true;
                        })
                }
            >
                <Report size={24} className="nav-item-contents" />
                <div className="nav-item-contents" data-testid="reportMenu">Reports</div>
            </LeftNavItem>

            {/* Client 360 - only for standard customers with Source System Admin+ */}
            {isSourceSystemAdminOrHigher(userProfile) &&
             !userProfile.environmentConfig.cwInsightsCustomer &&
             process.env.REACT_APP_CLIENT360_ENABLED === 'true' && (
                <LeftNavItem linkUrl="/clients">
                    <Collaborate size={24} className="nav-item-contents" />
                    <div className="nav-item-contents">Client 360</div>
                </LeftNavItem>
            )}

            {/* Monitoring - uses unified canViewMonitoring function */}
            {canViewMonitoring(userProfile) && (
                <LeftNavItem
                    linkUrl="/monitoring"
                    expandable
                    subItems={
                        monitoringSubItems.filter((item) => {
                            // Report Usage Metrics - uses canViewUsageReport (includes Insights Engineer/Analyst)
                            if (item.label === 'Report Usage Metrics') {
                                return canViewUsageReport(userProfile);
                            }
                            // Batch Runs - standard customers only
                            if (item.label === 'Batch Runs') {
                                return !userProfile.environmentConfig.cwInsightsCustomer;
                            }
                            // Sync to ClientTrack / Canonical ID - highest admin only with syncToClientTrack enabled
                            if (item.label === 'Canonical ID Sync' || item.label === 'Sync to ClientTrack') {
                                return userProfile.environmentConfig.syncToClientTrack && isHighestAdmin(userProfile);
                            }
                            return false;
                        })
                    }
                >
                    <Activity size={24} className="nav-item-contents" />
                    <div className="nav-item-contents">Monitoring</div>
                </LeftNavItem>
            )}

            {/* Team Members - uses unified canManageTeamMembers function */}
            {canManageTeamMembers(userProfile) && (
                <LeftNavItem
                    linkUrl="/teammembers"
                    expandable={userProfile.isEccoviaUser}
                    subItems={teamMembersSubItems}
                >
                    <Events size={24} className="nav-item-contents" />
                    <div className="nav-item-contents">Team Members</div>
                </LeftNavItem>
            )}

            {/* Data Tools - only for standard customers */}
            {isSourceSystemAdminOrHigher(userProfile) && !userProfile.environmentConfig.cwInsightsCustomer && (
                <LeftNavItem linkUrl="/datatools">
                    <Tools size={24} className="nav-item-contents" />
                    <div className="nav-item-contents">Data Tools</div>
                </LeftNavItem>
            )}

            {/* Configuration - uses unified canAccessAdmin function */}
            {canAccessAdmin(userProfile) && (
                <LeftNavItem linkUrl="/admin">
                    <Settings size={24} className="nav-item-contents" />
                    <div className="nav-item-contents">Configuration</div>
                </LeftNavItem>
            )}
        </Nav>
    );
};

export default LeftNav;
